// These functions collapse the marketplace's foodItems into the entries that are
// displayed to shoppers.  Items that belong to a food grouping are gathered
// together so the user sees one card with a price range, while everything else
// is displayed on its own as it always has been.
import DEFAULT_FOOD_IMAGE from "./../../../images/DefaultFoodImage.js";

// Builds a dictionary of the grouping categories with the grouping's id as the
// key.  The filters use it to look up a food item's grouping category without
// searching the groupings for every item.
export function createGroupingCategories(foodGroupings) {
  const groupingCategories = {};
  (foodGroupings || []).forEach((grouping) => {
    if (grouping.category) {
      groupingCategories[grouping.id] = grouping.category;
    }
  });
  return groupingCategories;
}

// Checks whether a food item belongs in the category the user selected.  An item
// in a grouping is found under its own category and under its grouping's
// category, so grouped items still show up where they always did.
export function foodItemMatchesCategory(
  foodItem,
  selectedCategory,
  groupingCategories,
) {
  if (foodItem.category === selectedCategory) {
    return true;
  }

  if (!groupingCategories || !foodItem.foodGrouping) {
    return false;
  }

  return groupingCategories[foodItem.foodGrouping] === selectedCategory;
}

// Adds the grouping categories to the marketplace's list of categories.  Only
// the groupings that one of the marketplace's items actually belongs to are
// added, so a location doesn't list a category it has nothing to sell in.
export function addGroupingCategories(
  marketplaceCategories,
  foodItems,
  foodGroupings,
) {
  const categories = [...(marketplaceCategories || [])];
  const groupingCategories = createGroupingCategories(foodGroupings);

  (foodItems || []).forEach((foodItem) => {
    const category = groupingCategories[foodItem.foodGrouping];
    if (category && !categories.includes(category)) {
      categories.push(category);
    }
  });

  return categories;
}

// Picks the image that represents a grouping on the marketplace.  The grouping's
// own image is used when it has one.  Otherwise the cheapest version that has an
// image of its own represents the grouping, and when none of them have one the
// default marketplace image is used.
export function getGroupingImage(grouping, variants) {
  if (grouping?.displayImage) {
    return grouping.displayImage;
  }

  // The variants are already ordered from the cheapest to the most expensive.
  const variantWithImage = (variants || []).find(
    (variant) => !!variant.displayImage,
  );

  if (variantWithImage) {
    return variantWithImage.displayImage;
  }

  return DEFAULT_FOOD_IMAGE;
}

// Shared by product cards, grouped price ranges, and spreadsheet exports.
export function getShopperPrice(row, userInfo, pickupLocationDetails) {
  const price = row.suggestedPrice
    ? 0
    : Number((parseFloat(row.price) || 0).toFixed(2));

  // Only individuals are charged the individual price, everyone else uses the
  // distribution price.
  if (!userInfo || userInfo.userType !== "individual") {
    return price;
  }

  const distributionQuantity = parseFloat(row.distributionQuantity) || 1;
  const individualQuantity = parseFloat(row.individualQuantity) || 1;

  // Round the individual portion before fees, as on the product card.
  let userPrice = Number(
    (price / (distributionQuantity / individualQuantity)).toFixed(2),
  );

  // The package fee is added on top of the item's price.
  const packageFee = parseFloat(row.packageFee);
  if (packageFee > 0) {
    userPrice += packageFee;
  }

  // The volunteerPercentage lives on the main distribution location.  Community
  // hubs nest that location under a pickupLocation key, so look there first
  // before falling back to the top level details.
  let volunteerPercentage;
  if (pickupLocationDetails) {
    if (
      pickupLocationDetails.pickupLocation?.volunteerPercentage !== undefined
    ) {
      volunteerPercentage = parseFloat(
        pickupLocationDetails.pickupLocation.volunteerPercentage,
      );
    } else if (pickupLocationDetails.volunteerPercentage !== undefined) {
      volunteerPercentage = parseFloat(
        pickupLocationDetails.volunteerPercentage,
      );
    }
  }
  if (volunteerPercentage && volunteerPercentage > 0) {
    userPrice += userPrice * (volunteerPercentage / 100);
  }

  return userPrice;
}

// Turns the displayed foodItems into the list of entries the marketplace renders.
// An entry is either a single foodItem or a grouping holding every version of
// that item, ordered from the cheapest to the most expensive.  A grouping takes
// the position of its cheapest listed item so the user's chosen sort order is
// still respected.
export function createMarketplaceEntries(
  foodItems,
  foodGroupings,
  userInfo,
  pickupLocationDetails,
) {
  const entries = [];

  // Nothing to group when there are no groupings to match against.
  if (!foodGroupings || foodGroupings.length === 0) {
    (foodItems || []).forEach((foodItem, index) => {
      entries.push({ type: "item", item: foodItem, index });
    });
    return entries;
  }

  const groupingsById = {};
  foodGroupings.forEach((grouping) => {
    groupingsById[grouping.id] = grouping;
  });

  // Tracks where each grouping's entry was placed so later versions of the same
  // item are added to the entry that already exists.
  const entryIndexByGrouping = {};

  (foodItems || []).forEach((foodItem, index) => {
    const grouping = groupingsById[foodItem.foodGrouping];

    // Items without a grouping, or whose grouping has since been deleted, are
    // displayed on their own.  The item's position in the foodItems array is
    // kept because the basket functions use it to find the item again.
    if (!grouping) {
      entries.push({ type: "item", item: foodItem, index });
      return;
    }

    const existingIndex = entryIndexByGrouping[grouping.id];
    if (existingIndex === undefined) {
      entryIndexByGrouping[grouping.id] = entries.length;
      entries.push({
        type: "group",
        id: grouping.id,
        grouping,
        variants: [foodItem],
      });
    } else {
      entries[existingIndex].variants.push(foodItem);
    }
  });

  // Now that every version has been gathered, order them by price and work out
  // the range to display on the grouping's card.
  entries.forEach((entry) => {
    if (entry.type !== "group") {
      return;
    }
    entry.variants.sort(
      (a, b) =>
        getShopperPrice(a, userInfo, pickupLocationDetails) -
        getShopperPrice(b, userInfo, pickupLocationDetails),
    );
    entry.minPrice = getShopperPrice(
      entry.variants[0],
      userInfo,
      pickupLocationDetails,
    );
    entry.maxPrice = getShopperPrice(
      entry.variants[entry.variants.length - 1],
      userInfo,
      pickupLocationDetails,
    );
  });

  return entries;
}
